import { isSamePublishClass } from '@/shared/utils/publish';
import { usePublishedBoxStore, publishQueries } from '@/entities/sets/publish';
import { usePersistStore, useSearchStore } from '@/shared/lib/store';
import { NoDataNew } from '@/shared/ui/no-data';
import { ByClassStudentFilter } from '@/widgets/filter';
import { ByClassStudentTable } from '@/widgets/table-list';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useMemo, useEffect } from 'react';

interface ByClassStudentTabProps {
    onDetailProps: (stntData: { claName: string; userId: string; name: string }) => void;
}
export const ByClassStudentTab: FC<ByClassStudentTabProps> = ({ onDetailProps }) => {
    const queryClient = useQueryClient();
    const { inKeyword } = useSearchStore();
    const { sUserId } = usePersistStore();
    const {
        searchKeyword,
        searchPage,
        searchUserId,
        setSearchUserId,
        selectClass,
        setSelectClass,
        setSearchPage,
        claIdList,
        filterClassList,
        checkKeyList,
        claIdListData,
    } = usePublishedBoxStore();

    const searchClaIdList = selectClass
        ? (claIdList
              ?.filter((e) => isSamePublishClass(e.claId, selectClass.claId))
              .map((e) => e.claId) ?? [])
        : [];

    const queryParams = useMemo(
        () => ({
            wrterId: sUserId,
            claIds: selectClass ? (searchClaIdList?.join(',') ?? '') : '',
            stdtUserIds:
                searchUserId?.length > 0
                    ? (searchUserId.join(',') ?? '')
                    : inKeyword === ''
                      ? ''
                      : 'no-user',
            page: searchPage,
            size: 30,
        }),
        [sUserId, searchClaIdList, searchKeyword, searchPage, selectClass]
    );

    const { data, isFetched } = publishQueries.list.getPublishStntList(queryParams, !!selectClass);

    useEffect(() => {
        setSearchPage(0);
    }, [selectClass]);

    useEffect(() => {
        const firstClassInfo = filterClassList[0]?.classInfo;
        const selectedClassSeq = selectClass?.claId.split('-')[4];
        const selectedClassExists = filterClassList.some(
            (classItem) => classItem.classInfo.classSeq.toString() === selectedClassSeq
        );

        if (selectedClassExists) return;

        if (!firstClassInfo) {
            if (selectClass) setSelectClass(undefined);
            return;
        }

        const { schName, classSeq, schCode, crtYear, schYear } = firstClassInfo;
        const processedSchName = schName?.replace('등학교', '');
        const claId = 'viva-' + schCode + '-' + crtYear + '-' + schYear + '-' + classSeq;

        setSelectClass({ claId, name: processedSchName });
    }, [filterClassList, selectClass, setSelectClass]);

    useEffect(() => {
        return () => {
            setSearchUserId([]);
            queryClient.removeQueries({
                queryKey: ['publish-list', 'stnt-list'],
            });
        };
    }, []);

    return (
        <>
            <ByClassStudentFilter
                selectClass={selectClass}
                setSelectClass={setSelectClass}
                filterClassList={filterClassList}
                checkKeyList={checkKeyList}
                claIdListData={claIdListData}
            />
            {data && data?.publishList?.length > 0 ? (
                <ByClassStudentTable data={data} onDetailProps={onDetailProps} />
            ) : isFetched ? (
                <NoDataNew
                    titleBlue={searchKeyword}
                    titleBlack="검색 결과가 없습니다."
                    type={'keyword'}
                />
            ) : (
                <NoDataNew titleBlue="준비 중입니다." />
            )}
        </>
    );
};
